import { ICertificateContext, ItemTypeEnum, IUserCertificate } from '@share/common-types';

import {
  filterUserCertificatesByTypeAndStatus,
  findCertificateItemIdForItemType,
  findUserCertificateById,
} from './certificate-search-bootstrap';

export interface BuildCertificateContextParams {
  certificateId: string | undefined;
  certificateItemId?: string | undefined;
  certificates: IUserCertificate[] | undefined;
  itemType: ItemTypeEnum;
}

export function buildCertificateContext(
  params: BuildCertificateContextParams,
): ICertificateContext | undefined {
  const cid = params.certificateId?.trim();
  if (!cid) {
    return undefined;
  }
  const availableCertificates = filterUserCertificatesByTypeAndStatus(
    params.certificates,
    params.itemType,
  );
  const certificate = findUserCertificateById(availableCertificates, cid);
  if (!certificate) {
    return undefined;
  }
  let itemId = params.certificateItemId?.trim();
  if (!itemId) {
    itemId = findCertificateItemIdForItemType(certificate, params.itemType)?.trim();
  }
  const ctx: ICertificateContext = { certificateId: cid };
  if (itemId) {
    ctx.certificateItemId = itemId;
  }
  return ctx;
}

/**
 * Builds {@link ICertificateContext} from explicit ids only (no lookup in loaded certificates).
 * Use when certificates are not loaded yet or after reload — server validates the ids.
 */
export function buildCertificateContextFromKnownIds(
  certificateId: string | undefined,
  certificateItemId?: string | undefined,
): ICertificateContext | undefined {
  const cid = certificateId?.trim();
  if (!cid) {
    return undefined;
  }
  const ctx: ICertificateContext = { certificateId: cid };
  const itemId = certificateItemId?.trim();
  if (itemId) {
    ctx.certificateItemId = itemId;
  }
  return ctx;
}

/** Prefer list-backed context; fall back to known ids so requests still send certificateContext after reload. */
export function buildCertificateContextWithPersistedFallback(
  params: BuildCertificateContextParams,
): ICertificateContext | undefined {
  const fromList = buildCertificateContext(params);
  if (fromList) {
    return fromList;
  }
  return buildCertificateContextFromKnownIds(params.certificateId, params.certificateItemId);
}

export interface BuildCertificateContextFromAppStateParams {
  certificates: IUserCertificate[] | undefined;
  itemType: ItemTypeEnum;
  storeCertificateId: string | undefined;
  storeCertificateItemId: string | undefined;
  routeCertificateId?: string | undefined;
  routeCertificateItemId?: string | undefined;
}

export function buildCertificateContextFromSelection(
  params: BuildCertificateContextFromAppStateParams,
): ICertificateContext | undefined {
  const certificateId = params.routeCertificateId?.trim() || params.storeCertificateId?.trim();
  const certificateItemId =
    params.routeCertificateItemId?.trim() || params.storeCertificateItemId?.trim();
  return buildCertificateContextWithPersistedFallback({
    certificateId,
    certificateItemId,
    certificates: params.certificates,
    itemType: params.itemType,
  });
}

export function attachCertificateContextToBody(
  body: Record<string, unknown>,
  context: ICertificateContext | undefined,
): void {
  if (!context) {
    return;
  }
  body.certificateContext = context;
}

/** Optional navigation / explicit override merged with filters store inside thunks. */
export interface ICertificateRouteHint {
  certificateId?: string;
  certificateItemId?: string;
}

export function readCertificateRouteHintFromLocation(
  locationState: unknown,
): ICertificateRouteHint | undefined {
  const s = locationState as ICertificateRouteHint | undefined;
  const certificateId = s?.certificateId?.trim();
  const certificateItemId = s?.certificateItemId?.trim();
  if (!certificateId) {
    return undefined;
  }
  return { certificateId, certificateItemId };
}

/** Merge router state with hotel filters so certificate survives reload / coupon refresh without history.state. */
export function mergeHotelCertificateRouteHint(
  locationState: unknown,
  filtersCertificate: { certificateId: string; certificateItemId: string },
): ICertificateRouteHint | undefined {
  const route = readCertificateRouteHintFromLocation(locationState);
  const certificateId = route?.certificateId || filtersCertificate.certificateId?.trim();
  if (!certificateId) {
    return undefined;
  }
  const certificateItemId =
    route?.certificateItemId || filtersCertificate.certificateItemId?.trim();
  return { certificateId, ...(certificateItemId ? { certificateItemId } : {}) };
}

/** Merge router state with condo filters so certificate survives reload without history.state. */
export function mergeCondoCertificateRouteHint(
  locationState: unknown,
  condoFiltersCertificate: { certificateId: string; certificateItemId: string },
): ICertificateRouteHint | undefined {
  const route = readCertificateRouteHintFromLocation(locationState);
  const certificateId = route?.certificateId || condoFiltersCertificate.certificateId?.trim();
  if (!certificateId) {
    return undefined;
  }
  const certificateItemId =
    route?.certificateItemId || condoFiltersCertificate.certificateItemId?.trim();
  return { certificateId, ...(certificateItemId ? { certificateItemId } : {}) };
}
